import React, { useEffect, useState, useRef, useContext } from "react";
import api from "../../utils/axiosInstance";
import { Link } from "react-router-dom";
import { User, LogOut } from "lucide-react";
import LogoutConfirmModal from "../Modals/LogoutConfirmModal";
import { motion, AnimatePresence } from "framer-motion";
import { AuthContext } from "../../context/AuthContext";

const Navbar = ({ sidebarOpen = false }) => {
  const { user } = useContext(AuthContext); // single source of truth
  const [profile, setProfile] = useState(null); // from /admin-details
  const [open, setOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      return;
    }
    const endpoint = user?.roleSlug === "vendor" ? "/vendor-self" : "/admin-details";
    api
      .get(endpoint)
      .then((res) => {
        setProfile(res.data?.data || res.data);
      })
      .catch(() => {
        setProfile(null);
      });
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const handleLogout = () => {
    setProfile(null);
    setShowLogoutConfirm(false);
    setOpen(false);
  };

  const handleMenuClick = (callback) => {
    setOpen(false);
    if (callback) callback();
  };

  // Avatar (fallback to initials if no image)
  const getDisplayName = () => {
    if (user?.roleSlug === "vendor") {
      // username from admin_auth table (via /auth/me)
      return (
        user?.username ||
        profile?.company_name ||
        (profile?.primary_users
          ? `${profile.primary_users.first_name || ""} ${profile.primary_users.last_name || ""}`.trim()
          : null) ||
        user?.email ||
        "V"
      );
    }
    return profile?.username || user?.username || user?.email || "U";
  };

  // Avatar (fallback to initials if no image)
  const getAvatar = () => {
    if (profile?.avatarUrl) {
      return (
        <img
          src={profile.avatarUrl}
          alt="avatar"
          className="w-8 h-8 rounded-full object-cover border border-gray-600"
        />
      );
    }
    const displayName = getDisplayName();
    const initial = displayName?.[0]?.toUpperCase() || "U";
    return (
      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
        {initial}
      </div>
    );
  };

  return (
    <>
      <nav className="sticky top-0 bg-gray-900 text-white px-6 py-3 flex justify-between items-center  z-50">
        {/* Left Section: Logo/Brand */}
        <Link to="/" className="flex items-center group" aria-label="Globilise">
          <div
            className={`relative h-8 overflow-hidden transition-[width] duration-500 ease-in-out
              ${sidebarOpen ? "w-[180px]" : "w-8"}`}
          >
            <img
              src="/globilise-logo.png"
              alt="Globilise"
              className="h-8 max-w-none block absolute top-0 left-0 group-hover:opacity-90 transition-opacity"
            />
          </div>
        </Link>

        {/* Right Section */}
        <div className="flex items-center gap-4">
          {/* Vendor context label */}
          {user?.roleSlug === "vendor" && profile?.type && (
            <div className="text-sm text-gray-300">
              <span className="font-bold text-lg text-white">{profile.type}</span>{" "}
              Vendor of{" "}
              
                {profile.admin?.details
                  ? (profile.admin.details.account_type === "company"
                      ? profile.admin.details.company_name
                      : `${profile.admin.details.first_name} ${profile.admin.details.last_name}`)
                  : "—"}
              
            </div>
          )}
          {!user ? (
            <>
              <Link
                to="/login"
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg shadow-md transition"
              >
                Login
              </Link>
              <Link
                to="/create-account"
                className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg shadow-md transition border border-gray-500"
              >
                Create an Account
              </Link>
            </>
            
          ) : (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-2 bg-gray-800 px-3 py-2 rounded-full hover:bg-gray-700 transition"
              >
                {getAvatar()}
                <span className="hidden sm:inline font-medium">
                  {getDisplayName()}
                </span>
              </button>

              <AnimatePresence>
                {open && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg overflow-hidden text-gray-800"
                  >
                    <div className="px-4 py-3 border-b bg-gray-50">
                      <p className="font-semibold">{getDisplayName()}</p>
                      <p className="text-sm text-gray-500">
                        {user?.email || ""}
                      </p>
                    </div>

                    <Link
                      to="/profile"
                      onClick={() => handleMenuClick()}
                      className="flex items-center gap-2 px-4 py-3 text-sm hover:bg-gray-100 transition"
                    >
                      <User className="w-4 h-4" />
                      My Profile
                    </Link>

                    <button
                      onClick={() =>
                        handleMenuClick(() => setShowLogoutConfirm(true))
                      }
                      className="flex items-center gap-2 px-4 py-3 text-sm w-full text-left hover:bg-gray-100 transition"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </nav>

      {showLogoutConfirm && (
        <LogoutConfirmModal
          onCancel={() => setShowLogoutConfirm(false)}
          onLogout={handleLogout}
        />
      )}
    </>
  );
};

export default Navbar;
